import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Activity, ShieldCheck, Sparkles, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("ddankwa@scensob.com");
  const [password, setPassword] = useState("Careiq!2026");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome to CAREIQ");
      // redirect handled declaratively by `if (user) return <Navigate/>`
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#0B1220] text-white">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden p-12 lg:flex"
        style={{ background: "radial-gradient(1200px 600px at 20% 10%, #155EEF22, transparent), radial-gradient(900px 500px at 90% 90%, #7C3AED22, transparent), #0B1220" }}>
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#155EEF] shadow-lg shadow-[#155EEF]/40">
            <Activity size={24} strokeWidth={2.4} />
          </span>
          <div>
            <div className="text-xl font-bold tracking-tight">CAREIQ</div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Human Services OS</div>
          </div>
        </div>

        <div className="max-w-md">
          <h1 className="text-4xl font-bold leading-tight tracking-tight">
            One platform. Complete visibility. Intelligent action.
          </h1>
          <p className="mt-4 text-slate-400">
            An AI-first operating system for residential human services — unifying care, workforce,
            compliance and operations into a single mission-control experience.
          </p>
          <div className="mt-8 space-y-3">
            {[
              [Sparkles, "AI Copilot & executive briefings grounded in your data", "#7C3AED"],
              [ShieldCheck, "Compliance nervous system with audit-readiness", "#12B76A"],
              [Activity, "Cross-module intelligence across every workflow", "#155EEF"],
            ].map(([Icon, text, col], i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-slate-300">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ backgroundColor: `${col}22`, color: col }}>
                  <Icon size={16} />
                </span>
                {text}
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-slate-600">Demo environment · synthetic data only · no real PHI</div>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-2.5">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#155EEF]"><Activity size={22} /></span>
              <span className="text-lg font-bold">CAREIQ</span>
            </div>
          </div>
          <h2 className="text-2xl font-bold tracking-tight">Sign in</h2>
          <p className="mt-1 text-sm text-slate-400">Access your organization's command center.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">Email</label>
              <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-11 w-full rounded-md border border-white/10 bg-white/5 px-3 text-sm outline-none transition-colors focus:border-[#155EEF] focus:ring-2 focus:ring-[#155EEF]/30"
                placeholder="you@organization.com" required />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">Password</label>
              <div className="relative">
                <input data-testid="login-password-input" type={show ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 w-full rounded-md border border-white/10 bg-white/5 px-3 pr-10 text-sm outline-none transition-colors focus:border-[#155EEF] focus:ring-2 focus:ring-[#155EEF]/30"
                  placeholder="••••••••" required />
                <button type="button" onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button data-testid="login-submit-btn" type="submit" disabled={loading}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#155EEF] text-sm font-semibold shadow-lg shadow-[#155EEF]/30 transition-colors hover:bg-[#0F4FD1] disabled:opacity-60">
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              {loading ? "Signing in…" : "Sign in to CAREIQ"}
            </button>
          </form>

          <div className="mt-6 rounded-lg border border-white/10 bg-white/5 p-3 text-xs text-slate-400">
            <div className="font-semibold text-slate-300">Demo accounts (password: Careiq!2026)</div>
            <div className="mt-1 grid grid-cols-2 gap-1">
              <span>manager@beacon.demo</span><span>nurse@beacon.demo</span>
              <span>dsp@beacon.demo</span><span>compliance@beacon.demo</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
